import type { AssignmentApp } from "@/lib/data/requests/matters/matter-task";
import type { DirectoryUser } from "@/lib/types";

export type MentionOption = {
  id: string;
  display: string;
  searchValue: string;
  email?: string;
  user?: DirectoryUser;
  selectable?: boolean;
};

export type MentionSegment = {
  value: string;
  option?: MentionOption;
};

export const mentionHighlightChipClassName =
  "bg-primary/10 text-primary inline-flex items-center rounded px-1.5 py-0.5 font-medium";

const buildMentionDisplay = (user: DirectoryUser) => {
  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  if (fullName.length > 0) {
    return `@${fullName}`;
  }

  if (user.email) {
    return `@${user.email}`;
  }

  return "@Unknown User";
};

export const buildMentionOptions = (
  users: DirectoryUser[],
  apps: AssignmentApp[] = [],
): MentionOption[] =>
  [
    ...users.map((user) => {
      const display = buildMentionDisplay(user);
      const nameParts = [user.firstName, user.lastName]
        .filter(Boolean)
        .join(" ");

      return {
        id: user.id,
        display,
        email: user.email,
        user,
        searchValue: `${display} ${nameParts} ${user.email}`.toLowerCase(),
      };
    }),
    ...apps.map((app) => ({
      id: `app:${app.integrationAppId}`,
      display: `@${app.integrationAppName || "Unavailable app"} (app)`,
      searchValue: `${app.integrationAppName ?? ""} app`.toLowerCase(),
    })),
  ].sort((first, second) =>
    first.display.localeCompare(second.display, undefined, {
      sensitivity: "base",
    }),
  );

export const convertBackendMessageToDisplay = (
  message: string,
  options: MentionOption[],
) => {
  if (!message || options.length === 0) return message;

  return message.replace(/{{(.*?)}}/g, (match, id: string) => {
    const option = options.find((opt) => opt.id === id);
    return option ? option.display : match;
  });
};

export const convertDisplayMessageToBackend = (
  message: string,
  options: MentionOption[],
) => {
  if (!message || options.length === 0) return message;

  const sortedOptions = [...options].sort(
    (first, second) => second.display.length - first.display.length,
  );

  let result = message;

  for (const option of sortedOptions) {
    if (option.display.length === 0) continue;
    if (!result.includes(option.display)) continue;
    result = result.split(option.display).join(`{{${option.id}}}`);
  }

  return result;
};

export const messageHasBackendMentions = (message: string) =>
  /{{[^}]+}}/.test(message);

export const getMentionSegments = (
  message: string,
  options: MentionOption[],
): MentionSegment[] => {
  if (!message) {
    return [];
  }

  if (options.length === 0) {
    return [{ value: message }];
  }

  const matches: Array<{ start: number; end: number; option: MentionOption }> =
    [];
  const sortedOptions = [...options].sort(
    (first, second) => second.display.length - first.display.length,
  );

  for (const option of sortedOptions) {
    const display = option.display;
    if (!display) continue;

    let searchStart = 0;
    while (searchStart < message.length) {
      const index = message.indexOf(display, searchStart);
      if (index === -1) break;
      const end = index + display.length;

      const overlaps = matches.some(
        (match) => index < match.end && end > match.start,
      );
      if (!overlaps) {
        matches.push({ start: index, end, option });
      }
      searchStart = end;
    }
  }

  if (matches.length === 0) {
    return [{ value: message }];
  }

  matches.sort((first, second) => first.start - second.start);

  const segments: MentionSegment[] = [];
  let cursor = 0;

  matches.forEach((match) => {
    if (cursor < match.start) {
      segments.push({ value: message.slice(cursor, match.start) });
    }

    segments.push({
      value: message.slice(match.start, match.end),
      option: match.option,
    });
    cursor = match.end;
  });

  if (cursor < message.length) {
    segments.push({ value: message.slice(cursor) });
  }

  return segments;
};
